import { memo, FC, useState, useMemo } from 'react'
import { Select, Form, Input, Button } from 'antd'
import styles from './index.module.scss'
import { useTranslation } from '@/services/i18n'
import { ReceiverInfo, useCalcUsdValue, useGetTxInitData } from '@/hooks/tx'
import AdvancedSetting from './AdvancedSetting'
import GasSetting, { GasSettingData } from './GasSetting'
import { useParams } from 'react-router-dom'
import { FeeRateDto, CreateTxDTO } from '@/services/apis/tx/models'
import CoinLogo from '../CoinLogo'
import { useEstimateFee, useLoopedFeeLevel } from '@/hooks/tx'
import { getLevelFeeRate } from '@/utils/fee'
import { FeeLevelTypeEnum, SourceTypeEnum } from '@/constants/enum'
import BigNumber from 'bignumber.js'
import { useCoinBasis } from '@/hooks/coin'
import SendNumberInput from './SendNumberInput'
import { DestinationTypeEnum } from '@/constants/enum'
import riskApi from '@/services/apis/risk'
import TxSendSuccessModal from '@/components/TxSendSuccessModal'
import txApi from '@/services/apis/tx'
import Warning from '@/assets/images/warning.png'
const { TextArea } = Input

interface ConfirmReceiverProps {
  onClose: () => void
  coinKey: string
  receiver: ReceiverInfo
}

const ConfirmReceiver: FC<ConfirmReceiverProps> = ({ onClose, receiver, coinKey }) => {
  const destinationType = receiver.realDestionType
  const [feeType, setFeeType] = useState<'level' | 'custom'>('level')
  const [feeLevel, setFeeLevel] = useState<FeeLevelTypeEnum>(FeeLevelTypeEnum.MIDDLE)
  const [feeRate, setFeeRate] = useState<FeeRateDto>()
  const { t } = useTranslation()
  const coinBasis = useCoinBasis(coinKey)
  const feeCoinBasis = useCoinBasis(coinBasis?.feeCoinKey)
  const [sendAmount, setSendAmount] = useState('')
  const [note, setNote] = useState('')
  const [isSending, setIsSending] = useState(false)

  const [nonce, setNonce] = useState('')
  const [isRbf, setIsRbf] = useState(false)

  const [hasError, setHasError] = useState(false)

  const [isOpenAdvancedSetting, setOpenAdvancedSetting] = useState(false)
  const [isOpenGasSetting, setOpenGasSetting] = useState(false)
  const { walletKey } = useParams()

  const {
    expression,
    accountBaseAddressDto: sourceAccountInfo,
    feeRateLevelDto: initFeeLevelModel,
  } = useGetTxInitData(coinKey, walletKey!, receiver.realAddr) || {}
  const hasGasSetting = useMemo(() => {
    return ['eth', 'utxo', 'fil', 'sui', 'aptos'].includes(coinBasis?.coinImplTag ?? '')
  }, [coinBasis])
  // 预估手续费
  const { estimateFee, getFeeRateText } = useEstimateFee(coinKey)
  // 实时刷新手续费
  const { feeLevelModel, countDown } = useLoopedFeeLevel({
    coinKey,
    replaceTxHash: '',
    sourceAddress: sourceAccountInfo?.baseAddress,
    destinationAddress: receiver.realAddr,
  })
  // 手续费率
  const realFeeRate = useMemo(() => {
    if (feeType === 'level') {
      return getLevelFeeRate(feeLevel, feeLevelModel ? feeLevelModel : initFeeLevelModel)
    }
    // 如果是自定义手续费汇率
    return feeRate
  }, [feeType, feeLevel, feeLevelModel, initFeeLevelModel, feeRate])
  // 预估手续费
  const estimatedFee = useMemo<[string, string]>(
    () => estimateFee(realFeeRate, expression),
    [realFeeRate, expression, estimateFee]
  )
  const feeUsdValue = useCalcUsdValue(estimatedFee[0], feeCoinBasis?.exchangeRateUsd)
  // 费率文案
  const feeRateText = useMemo(() => getFeeRateText(realFeeRate), [realFeeRate, getFeeRateText])

  const feeIsEnough = useMemo(() => {
    if (!sourceAccountInfo || !estimatedFee[0]) {
      return true
    }
    return new BigNumber(sourceAccountInfo.feeCoinBalance).isGreaterThanOrEqualTo(
      new BigNumber(estimatedFee[0])
    )
  }, [sourceAccountInfo, estimatedFee[0]])

  const coinOption = useMemo(() => {
    return [
      {
        label: (
          <div className={styles.optionLike}>
            <CoinLogo logo={coinBasis?.logoUrl} networkLogo={coinBasis?.networkLogo} />
            <div className={styles.detail}>
              <div>
                <span className={styles.coinName} style={{ paddingLeft: '0px' }}>
                  {coinBasis?.coinName}
                </span>
                <span className={styles.coinFullName}>({coinBasis?.coinFullName})</span>
              </div>
              <div style={{ marginTop: '4px' }}>{coinBasis?.networkName}</div>
            </div>
          </div>
        ),
        value: coinBasis?.coinKey,
        key: coinBasis?.coinKey,
      },
    ]
  }, [coinBasis])
  const advancedSettingType = useMemo(() => {
    if (['eth', 'aptos'].includes(coinBasis?.coinImplTag ?? '')) {
      return 'evm'
    }
    if (sourceAccountInfo?.supportRbf) {
      return 'btc'
    }
  }, [sourceAccountInfo, coinBasis])

  const tradeParams = useMemo<CreateTxDTO>(() => {
    const params: CreateTxDTO = {
      coinKey,
      value: sendAmount,
      feeRateDto: realFeeRate!,
      sourceId: walletKey!,
      sourceType: SourceTypeEnum.VAULT_ACCOUNT,
      destinationType,
      destinationId: receiver.realDestinationId,
      destinationAddress: receiver.realAddr,
      note,
      riskNo: '',
      failOnContract: false,
    }
    if (feeType === 'level' && feeLevel) {
      params.feeLevel = feeLevel.toLowerCase()
    }
    if (nonce && ['eth', 'aptos'].includes(coinBasis?.coinImplTag ?? '')) {
      params.nonce = Number(nonce)
    }
    if (coinBasis?.coinImplTag === 'utxo') {
      params.isRbf = isRbf ? 1 : 0
    }
    return params
  }, [sendAmount, note, coinKey, realFeeRate, walletKey, destinationType, receiver, isRbf, nonce])

  const onSend = async () => {
    setIsSending(true)
    // 风控埋点
    const riskRes = await riskApi.addPoint({
      coin_key: coinKey,
      amount: sendAmount,
      to_address:
        destinationType === DestinationTypeEnum.VAULT_ACCOUNT
          ? receiver.realDestinationId
          : receiver.realAddr,
      address_type: destinationType === DestinationTypeEnum.VAULT_ACCOUNT ? 2 : 1,
    })
    if (!riskRes.success || !riskRes.data) {
      setIsSending(false)
      return
    }
    const txRes = await txApi.createTx({
      ...tradeParams,
      riskNo: riskRes.data,
    })
    setIsSending(false)
    if (txRes.success) {
      onClose()
      TxSendSuccessModal.open()
      return
    }
  }
  const onGasSettingOk = (data: GasSettingData) => {
    if (data.feeType === 'level') {
      setFeeLevel(data.feeLevel!)
    }
    if (data.feeType === 'custom') {
      setFeeRate(data.feeRate)
    }
    setFeeType(data.feeType!)
    setOpenGasSetting(false)
  }
  return (
    <Form layout="vertical">
      <Form.Item label={t('wallet.sendDrawer.sendCoin')}>
        <Select
          options={coinOption}
          value={coinKey}
          className={styles.select}
          popupClassName={styles.popupSelect}
          showArrow={false}
          size="large"
          disabled
        />
      </Form.Item>
      <Form.Item label={t('wallet.sendDrawer.receiveAddress')}>
        <div>
          <div
            className={styles.addressBox}
            style={{ backgroundColor: receiver.isPhishing ? '#FFE4E4' : '#f4f4f4' }}
          >
            <div className={styles.addressBoxName}>{receiver.realName}</div>
            <div className={styles.addressContent}>{receiver.realAddr}</div>
          </div>
          {receiver.isPhishing && (
            <div>
              <img src={Warning} alt="" style={{ width: '18px', height: '18px' }} />
              <span style={{ color: '#E64646' }}> {t('record.fraudulentAddressContent1')}</span>
            </div>
          )}
        </div>
      </Form.Item>
      <SendNumberInput
        value={sendAmount}
        onChange={setSendAmount}
        coinKey={coinKey}
        accountInfo={sourceAccountInfo}
        estimatedFee={estimatedFee}
        onCallbackError={setHasError}
        isShowMax={!!coinBasis}
      />
      <Form.Item
        validateStatus={!feeIsEnough ? 'error' : ''}
        help={!feeIsEnough && t('fee.feeNotEnough')}
      >
        <div className={styles.label}>
          <span>{t('wallet.sendDrawer.gas')}</span>
          {hasGasSetting && (
            <>
              <Button
                type="link"
                onClick={() => setOpenGasSetting(true)}
                className={styles.gasLink}
              >
                {t('wallet.sendDrawer.gasSetting')}
              </Button>
              <GasSetting
                countDown={countDown}
                coinKey={coinKey}
                open={isOpenGasSetting}
                onClose={() => setOpenGasSetting(false)}
                onOk={onGasSettingOk}
                feeLevelModel={feeLevelModel || initFeeLevelModel}
                estimateFee={(feeRate?: FeeRateDto) => estimateFee(feeRate, expression)}
              />
            </>
          )}
        </div>
        <div className={styles.displayBox}>
          <span className={styles.estimatedFee}>
            {estimatedFee?.join?.(' ')}
            ($ {feeUsdValue})
          </span>
          <span>{feeRateText}</span>
        </div>
      </Form.Item>
      <Form.Item label={t('common.remark')}>
        <TextArea
          maxLength={200}
          rows={4}
          placeholder={t('wallet.sendDrawer.remarkPlaceholder') || ''}
          value={note}
          onChange={evt => setNote(evt.target.value)}
        />
      </Form.Item>
      {advancedSettingType && (
        <div className={styles.advancedSetting}>
          <Button type="link" onClick={() => setOpenAdvancedSetting(true)}>
            {t('wallet.sendDrawer.advancedSetting')}
          </Button>
          <AdvancedSetting
            open={isOpenAdvancedSetting}
            onClose={() => setOpenAdvancedSetting(false)}
            nonce={nonce}
            onSaveNonce={setNonce}
            isRbf={isRbf}
            setIsRbf={setIsRbf}
            settingType={advancedSettingType}
            coinKey={coinKey}
            address={sourceAccountInfo?.baseAddress ?? ''}
          />
        </div>
      )}
      <div className={styles.footer}>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          type="primary"
          disabled={!sendAmount || !feeIsEnough || hasError}
          onClick={onSend}
          loading={isSending}
        >
          {t('wallet.sendDrawer.confirm')}
        </Button>
      </div>
    </Form>
  )
}

export default memo(ConfirmReceiver)
